
import { useState, useMemo, useEffect, useCallback } from 'react';
import { getPatients, updatePatient, deletePatient, addLog, addPatientEditRequest } from '../../../utils/storage';
import { getUser } from '../../../utils/auth';
import { getDeviceInfo, getIPAddress } from '../../../utils/device';
import type { Patient } from '../../../types';

type PatientType = 'student' | 'faculty' | 'other';

export default function PatientList() {
  const user = getUser();
  const isAdminOrMaster = user?.role === 'admin' || user?.role === 'master_admin';

  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState<string>('all');
  const [filterPayment, setFilterPayment] = useState<string>('all');
  const [editModal, setEditModal] = useState<{ patient: Patient | null; show: boolean }>({
    patient: null,
    show: false,
  });
  const [deleteModal, setDeleteModal] = useState<{ patient: Patient | null; show: boolean }>({
    patient: null,
    show: false,
  });
  const [deleteReason, setDeleteReason] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    gender: 'Male' as 'Male' | 'Female' | 'Other',
    dateOfBirth: '',
    bloodGroup: '',
    email: '',
    contactNumber: '',
    whatsappNumber: '',
    address: '',
    college: '',
    department: '',
    yearDesignation: '',
  });
  const [editPatientType, setEditPatientType] = useState<PatientType>('student');
  const [editCollegeOption, setEditCollegeOption] = useState<'sona' | 'other'>('sona');
  const [editStudentYear, setEditStudentYear] = useState('');
  const [editDesignation, setEditDesignation] = useState('');
  const [editOtherCollege, setEditOtherCollege] = useState('');

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    const data = await getPatients();
    setPatients(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const filteredPatients = useMemo(() => {
    return patients.filter((p) => {
      const matchesSearch =
        !searchTerm ||
        p.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.contactNumber.includes(searchTerm);

      const matchesGender = filterGender === 'all' || p.gender === filterGender;
      const matchesPayment =
        filterPayment === 'all' || (p.paymentStatus || 'unpaid') === filterPayment;

      return matchesSearch && matchesGender && matchesPayment;
    });
  }, [patients, searchTerm, filterGender, filterPayment]);

  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const openEditModal = (patient: Patient) => {
    setEditFormData({
      name: patient.name,
      gender: patient.gender,
      dateOfBirth: patient.dateOfBirth,
      bloodGroup: patient.bloodGroup,
      email: patient.email || '',
      contactNumber: patient.contactNumber,
      whatsappNumber: patient.whatsappNumber || '',
      address: patient.address || '',
      college: patient.college,
      department: patient.department,
      yearDesignation: patient.yearDesignation,
    });

    const pType = patient.patientType || 'student';
    setEditPatientType(pType);

    if (pType === 'student') {
      setEditStudentYear(patient.studentYear || patient.yearDesignation);
      if (patient.college === 'SONA COLLEGE OF TECHNOLOGY') {
        setEditCollegeOption('sona');
        setEditOtherCollege('');
      } else {
        setEditCollegeOption('other');
        setEditOtherCollege(patient.college);
      }
    } else if (pType === 'faculty') {
      setEditDesignation(patient.designation || patient.yearDesignation);
      if (patient.college === 'SONA COLLEGE OF TECHNOLOGY') {
        setEditCollegeOption('sona');
        setEditOtherCollege('');
      } else {
        setEditCollegeOption('other');
        setEditOtherCollege(patient.college);
      }
    } else {
      setEditOtherCollege(patient.college);
    }

    setEditModal({ patient, show: true });
  };

  const handleEditSubmit = async () => {
    if (!editModal.patient || !user) return;
    setActionLoading(true);
    try {
      const age = calculateAge(editFormData.dateOfBirth);

      let finalCollege = '';
      if (editPatientType === 'other') {
        finalCollege = editOtherCollege;
      } else {
        finalCollege = editCollegeOption === 'sona' ? 'SONA COLLEGE OF TECHNOLOGY' : editOtherCollege;
      }

      let finalYearDesignation = '';
      if (editPatientType === 'student') {
        finalYearDesignation = editStudentYear;
      } else if (editPatientType === 'faculty') {
        finalYearDesignation = editDesignation;
      } else {
        finalYearDesignation = editFormData.yearDesignation;
      }

      const updatedPatient: Partial<Patient> = {
        name: editFormData.name,
        gender: editFormData.gender,
        dateOfBirth: editFormData.dateOfBirth,
        age,
        bloodGroup: editFormData.bloodGroup,
        email: editFormData.email || undefined,
        contactNumber: editFormData.contactNumber,
        whatsappNumber: editFormData.whatsappNumber || undefined,
        address: editFormData.address || undefined,
        college: finalCollege,
        department: editFormData.department,
        yearDesignation: finalYearDesignation,
        patientType: editPatientType,
        studentYear: editPatientType === 'student' ? editStudentYear : undefined,
        designation: editPatientType === 'faculty' ? editDesignation : undefined,
      };

      await updatePatient(editModal.patient.id, updatedPatient);

      const { browser, device } = getDeviceInfo();
      const ipAddress = await getIPAddress();

      await addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'Patient Edit',
        details: `Edited patient: ${editFormData.name} (${editModal.patient.id})`,
        ipAddress,
        deviceName: device,
        browser,
        timestamp: new Date().toISOString(),
      });

      setEditModal({ patient: null, show: false });
      setSuccessMsg('Patient updated successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
      await fetchPatients();
    } catch (err) {
      console.error('Edit error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (patient: Patient) => {
    setDeleteModal({ patient, show: true });
    setDeleteReason('');
  };

  const handleDeleteSubmit = async () => {
    if (!deleteModal.patient || !user) return;
    setActionLoading(true);
    try {
      if (isAdminOrMaster) {
        await deletePatient(deleteModal.patient.id);

        const { browser, device } = getDeviceInfo();
        const ipAddress = await getIPAddress();

        await addLog({
          id: Date.now().toString(),
          userId: user.id,
          username: user.username,
          action: 'Patient Delete',
          details: `Deleted patient: ${deleteModal.patient.name} (${deleteModal.patient.id}). Reason: ${deleteReason}`,
          ipAddress,
          deviceName: device,
          browser,
          timestamp: new Date().toISOString(),
        });

        setDeleteModal({ patient: null, show: false });
        setDeleteReason('');
        setSuccessMsg('Patient deleted successfully!');
        setTimeout(() => setSuccessMsg(''), 3000);
        await fetchPatients();
      } else {
        if (!deleteReason.trim()) return;

        const requestId = `PDR${Date.now()}`;
        await addPatientEditRequest({
          id: requestId,
          patientId: deleteModal.patient.id,
          requestType: 'delete',
          requestedBy: user.username,
          requestedAt: new Date().toISOString(),
          reason: deleteReason,
          status: 'pending',
        });

        const { browser, device } = getDeviceInfo();
        const ipAddress = await getIPAddress();

        await addLog({
          id: Date.now().toString(),
          userId: user.id,
          username: user.username,
          action: 'Patient Delete Request',
          details: `Requested deletion of patient: ${deleteModal.patient.name} (${deleteModal.patient.id}). Reason: ${deleteReason}`,
          ipAddress,
          deviceName: device,
          browser,
          timestamp: new Date().toISOString(),
        });

        setDeleteModal({ patient: null, show: false });
        setDeleteReason('');
        setSuccessMsg('Delete request submitted! An admin will review it.');
        setTimeout(() => setSuccessMsg(''), 4000);
      }
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-5">
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-base"></i>
          {successMsg}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-sm"></i>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, ID, or contact..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
            />
          </div>
          <select
            value={filterGender}
            onChange={(e) => setFilterGender(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-white cursor-pointer"
          >
            <option value="all">All Genders</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          <select
            value={filterPayment}
            onChange={(e) => setFilterPayment(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-white cursor-pointer"
          >
            <option value="all">All Payments</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>
      </div>

      {/* Patient List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <i className="ri-user-heart-line text-teal-500"></i>
            Registered Patients
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={fetchPatients}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 transition-colors cursor-pointer disabled:opacity-50"
              title="Refresh list"
            >
              <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
              <i className="ri-user-search-line text-5xl text-gray-200"></i>
            </div>
            <p className="text-sm font-medium text-gray-500">No patients found</p>
            <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">College &amp; Dept</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Registered</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-teal-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {patient.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{patient.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{patient.id}</span>
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full border border-teal-100">{patient.bloodGroup}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-700">{patient.contactNumber}</p>
                      <p className="text-xs text-gray-400">{patient.gender}, {patient.age} yrs</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-700 truncate max-w-xs">{patient.college}</p>
                      <p className="text-xs text-gray-400 truncate">{patient.department}</p>
                    </td>
                    <td className="px-6 py-4">
                      {(patient.paymentStatus || 'unpaid') === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          <i className="ri-checkbox-circle-fill"></i>Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          <i className="ri-time-line"></i>Unpaid
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-700">{formatDate(patient.registeredAt)}</p>
                      <p className="text-xs text-gray-400">by {patient.registeredBy}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {isAdminOrMaster && (
                          <button
                            onClick={() => openEditModal(patient)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 transition-colors cursor-pointer"
                            title="Edit Patient"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                        )}
                        <button
                          onClick={() => openDeleteModal(patient)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors cursor-pointer"
                          title={isAdminOrMaster ? 'Delete Patient' : 'Request Delete'}
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal.show && editModal.patient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 py-6 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full my-8">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Edit Patient</h3>
                <p className="text-sm text-gray-500 mt-0.5">Update patient information</p>
              </div>
              <button
                onClick={() => setEditModal({ patient: null, show: false })}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-500"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Patient Type</label>
                  <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
                    {(['student', 'faculty', 'other'] as PatientType[]).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setEditPatientType(type)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${
                          editPatientType === type ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input type="text" value={editFormData.name} onChange={(e) => setEditFormData((prev) => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <select value={editFormData.gender} onChange={(e) => setEditFormData((prev) => ({ ...prev, gender: e.target.value as 'Male' | 'Female' | 'Other' }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer">
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <input type="date" value={editFormData.dateOfBirth} onChange={(e) => setEditFormData((prev) => ({ ...prev, dateOfBirth: e.target.value }))} max={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Blood Group</label>
                    <select value={editFormData.bloodGroup} onChange={(e) => setEditFormData((prev) => ({ ...prev, bloodGroup: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer">
                      {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                    <input type="tel" value={editFormData.contactNumber} onChange={(e) => setEditFormData((prev) => ({ ...prev, contactNumber: e.target.value }))} maxLength={10} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number</label>
                    <input type="tel" value={editFormData.whatsappNumber} onChange={(e) => setEditFormData((prev) => ({ ...prev, whatsappNumber: e.target.value }))} maxLength={10} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input type="email" value={editFormData.email} onChange={(e) => setEditFormData((prev) => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input type="text" value={editFormData.address} onChange={(e) => setEditFormData((prev) => ({ ...prev, address: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {editPatientType !== 'other' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">College</label>
                      <div className="flex gap-2 mb-2">
                        <button type="button" onClick={() => setEditCollegeOption('sona')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${editCollegeOption === 'sona' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>SONA COLLEGE OF TECHNOLOGY</button>
                        <button type="button" onClick={() => setEditCollegeOption('other')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${editCollegeOption === 'other' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Others</button>
                      </div>
                      {editCollegeOption === 'other' && (
                        <input type="text" value={editOtherCollege} onChange={(e) => setEditOtherCollege(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="Enter college name" />
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Organisation</label>
                      <input type="text" value={editOtherCollege} onChange={(e) => setEditOtherCollege(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="Enter organisation name" />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                    <input type="text" value={editFormData.department} onChange={(e) => setEditFormData((prev) => ({ ...prev, department: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {editPatientType === 'student' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                      <select value={editStudentYear} onChange={(e) => setEditStudentYear(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer">
                        <option value="">Select year</option>
                        {['I','II','III','IV','Others'].map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  )}
                  {editPatientType === 'faculty' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                      <input type="text" value={editDesignation} onChange={(e) => setEditDesignation(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="e.g., Assistant Professor" />
                    </div>
                  )}
                  {editPatientType === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Year / Designation</label>
                      <input type="text" value={editFormData.yearDesignation} onChange={(e) => setEditFormData((prev) => ({ ...prev, yearDesignation: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="Enter year or designation" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={handleEditSubmit}
                disabled={actionLoading}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-70 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {actionLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</> : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditModal({ patient: null, show: false })}
                disabled={actionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.show && deleteModal.patient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
              {isAdminOrMaster ? 'Delete Patient' : 'Request Patient Deletion'}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              {isAdminOrMaster
                ? 'Are you sure you want to delete this patient? This action cannot be undone.'
                : 'Submit a request to delete this patient. An admin will review it.'}
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-5">
              <p className="text-sm font-semibold text-gray-800">{deleteModal.patient.name}</p>
              <p className="text-xs text-gray-500 mt-1">{deleteModal.patient.id} &bull; {deleteModal.patient.contactNumber}</p>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Deletion {!isAdminOrMaster && <span className="text-red-500">*</span>}
                {isAdminOrMaster && <span className="text-gray-400 font-normal"> (Optional)</span>}
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => { if (e.target.value.length <= 500) setDeleteReason(e.target.value); }}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm resize-none"
                placeholder={isAdminOrMaster ? 'Optional: Reason for deletion' : 'Explain why this patient should be deleted'}
                maxLength={500}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{deleteReason.length}/500</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteSubmit}
                disabled={actionLoading || (!isAdminOrMaster && !deleteReason.trim())}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {actionLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Processing...</> : (isAdminOrMaster ? 'Delete' : 'Submit Request')}
              </button>
              <button
                onClick={() => { setDeleteModal({ patient: null, show: false }); setDeleteReason(''); }}
                disabled={actionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
